/** Thread structure for the existing private Bag Group messages collection. */
export interface BagGroupPostLike {
  id: string;
  replyToId?: string;
  createdAt?: unknown;
  lastReplyAt?: unknown;
  /** Who wrote the latest reply. Stamped by the reply gateway since BG-5. */
  lastReplyByUid?: unknown;
  /** The activity just before lastReplyByUid's current run of replies began. */
  priorReplyAt?: unknown;
}

function millis(value: unknown): number {
  if (!value || typeof value !== 'object') return 0;
  const toMillis = (value as { toMillis?: unknown }).toMillis;
  if (typeof toMillis !== 'function') return 0;
  try {
    const result = toMillis.call(value);
    return typeof result === 'number' && Number.isFinite(result) && result >= 0 ? result : 0;
  } catch { return 0; }
}

export function bagGroupPostId(message: Pick<BagGroupPostLike, 'id' | 'replyToId'>): string {
  return message.replyToId || message.id;
}

export function bagGroupPostAnchor(postId: string): string {
  return `bag-post-${postId}`;
}

export function bagGroupPostLink(groupId: string, messageId: string, replyToId?: string): string {
  return `/bag-groups/${encodeURIComponent(groupId)}#${encodeURIComponent(bagGroupPostAnchor(replyToId || messageId))}`;
}

/**
 * Activity, not housekeeping, is the version a personal Done acts upon.
 *
 * With a viewer, it is the activity that viewer has not written herself: her
 * own reply does not reopen a post she filed (bag group plan v2, BG-5). The
 * reply gateway stamps lastReplyByUid and priorReplyAt for exactly this. A post
 * with no lastReplyByUid (every reply before BG-5) keeps the plain version.
 * Without a viewer, it is the post's own latest activity, used for ordering.
 */
export function bagGroupPostVersionMs(
  post: Pick<BagGroupPostLike, 'createdAt' | 'lastReplyAt' | 'lastReplyByUid' | 'priorReplyAt'> & { id?: string },
  viewerUid?: string,
): number {
  const created = millis(post.createdAt);
  if (viewerUid && typeof post.lastReplyByUid === 'string' && post.lastReplyByUid === viewerUid) {
    return Math.max(created, millis(post.priorReplyAt));
  }
  return Math.max(created, millis(post.lastReplyAt));
}

export function bagGroupPostsInOrder<T extends BagGroupPostLike>(messages: readonly T[]): T[] {
  const activity = new Map<string, number>();
  for (const message of messages) {
    const id = bagGroupPostId(message);
    activity.set(id, Math.max(activity.get(id) ?? 0, bagGroupPostVersionMs(message)));
  }
  return messages.filter((message) => !message.replyToId).sort((a, b) =>
    (activity.get(b.id) ?? 0) - (activity.get(a.id) ?? 0) || a.id.localeCompare(b.id));
}
